import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { categoryColor, categoryLabel, formatRelative, stripMentionIds } from "../lib/constants";
import { Wifi, WifiOff, AlertTriangle, Clock, Plus, ClipboardCheck, Sparkles, Search, Target, ArrowRight, PartyPopper, CalendarClock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useActivityStream } from "../lib/useActivityStream";
import SyncActivityCard from "../components/SyncActivityCard";
import AskBrainydo from "../components/AskBrainydo";
import { useAuth } from "@/lib/auth";
import QuickTaskList from "../components/dashboard/QuickTaskList";
import ProductivityPanel, { Heatmap } from "../components/dashboard/ProductivityPanel";
import WaitingOnYou from "../components/dashboard/WaitingOnYou";
import DidYouKnow from "../components/dashboard/DidYouKnow";
import GroupPulse from "../components/dashboard/GroupPulse";

const STAT_CARDS = [
  { key: "urgent_today",          label: "Urgent",            cardClass: "metric-card-urgent",    icon: "priority_high",   iconColor: "#ba1a1a" },
  { key: "pending_followups",     label: "Pending Follow-ups",cardClass: "metric-card-high",      icon: "sync_alt",        iconColor: "#943700" },
  { key: "new_enquiries",         label: "New Enquiries",     cardClass: "metric-card-new",       icon: "mail",            iconColor: "#004ac6" },
  { key: "opportunities",         label: "Opportunities",     cardClass: "metric-card-opportunity",icon: "stars",          iconColor: "#6b21a8" },
  { key: "deadline_commitments",  label: "Follow Ups",        cardClass: "metric-card-followup",  icon: "pending_actions", iconColor: "#15803d" },
  { key: "review_queue",          label: "Review Queue",      cardClass: "metric-card-review",    icon: "fact_check",      iconColor: "#b45309" },
  { key: "client_support",        label: "Client Support",    cardClass: "metric-card-active",    icon: "headset_mic",     iconColor: "#0369a1" },
  { key: "quotations_awaiting",   label: "Quotations",        cardClass: "metric-card-completed", icon: "request_quote",   iconColor: "#15803d" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const { user } = useAuth();
  const [feed, setFeed] = useState({ urgent: [], upcoming: [], recent_messages: [], attention: [], attention_total: 0 });
  const [insights, setInsights] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [waConnected, setWaConnected] = useState(false);
  const [waHealth, setWaHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, f, wa] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/feed"),
        api.get("/whatsapp/status"),
      ]);
      setStats(s.data);
      setFeed(f.data);
      setWaConnected(wa.data.connected);
    } catch {}
    finally { setLoading(false); }
  };

  // Metrics load separately so a slow insights query never delays the task lists.
  const loadInsights = () => api.get("/dashboard/insights").then((r) => setInsights(r.data)).catch(() => {});

  // Quiet refresh after a quick action: counts, lists and metrics.
  const refresh = async () => {
    try {
      const [s2, f2] = await Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/feed")]);
      setStats(s2.data);
      setFeed(f2.data);
    } catch {}
    loadInsights();
  };

  useEffect(() => { load(); loadInsights(); }, []);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) refresh(); }, 120000);
    window.addEventListener("brainydo:tasks-changed", refresh);
    return () => { clearInterval(id); window.removeEventListener("brainydo:tasks-changed", refresh); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dropFrom = (key) => (id) => setFeed((f) => ({
    ...f,
    [key]: f[key].filter((t) => t.id !== id),
    ...(key === "attention" ? { attention_total: Math.max(0, (f.attention_total || 1) - 1) } : {}),
  }));

  // Auto-mode health line: separate from load() so a hiccup here never blanks the dashboard.
  useEffect(() => {
    let alive = true;
    const fetchHealth = () => api.get("/whatsapp/health").then((r) => { if (alive) setWaHealth(r.data); }).catch(() => {});
    fetchHealth();
    const id = setInterval(fetchHealth, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [waConnected]);

  // After login, WA reconnect happens in the background. The initial load() above
  // captures waConnected=false and renders the "Connect WhatsApp" onboarding. We
  // poll /whatsapp/status while disconnected (cheap call) and the moment WA flips
  // to connected we reload the full dashboard so the user sees fresh stats/feed
  // (and useActivityStream activates) without needing a manual page refresh.
  useEffect(() => {
    if (waConnected) return; // Already up — nothing to watch
    const start = Date.now();
    const id = setInterval(async () => {
      // Give up after 60s — beyond that, Layout's 20s poll keeps things eventually consistent
      if (Date.now() - start > 60000) { clearInterval(id); return; }
      try {
        const r = await api.get("/whatsapp/status");
        if (r.data.connected) {
          clearInterval(id);
          await load();
        }
      } catch {}
    }, 2000);
    return () => clearInterval(id);
  }, [waConnected]);

  const seed = async () => {
    setSeeding(true);
    toast.info("Classifying sample messages with GPT…");
    try {
      const r = await api.post("/seed/sample-messages");
      toast.success(`Loaded ${r.data.seeded} sample messages`);
      await load();
    } catch {
      toast.error("Could not seed samples");
    } finally { setSeeding(false); }
  };

  const { counts, phase, dismiss } = useActivityStream({ enabled: waConnected });

  const empty = !stats.total_messages;

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-8 max-w-[1500px] mx-auto animate-pulse" data-testid="dashboard-loading">
        {/* Header skeleton */}
        <div className="space-y-2">
          <div className="h-9 w-44 bg-slate-200 rounded-lg" />
          <div className="h-4 w-80 bg-slate-100 rounded" />
        </div>

        {/* KPI grid skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-xl h-24 p-4 flex flex-col justify-between">
              <div className="h-2.5 w-20 bg-slate-200 rounded" />
              <div className="flex justify-between items-end">
                <div className="h-7 w-10 bg-slate-200 rounded" />
                <div className="h-5 w-5 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>

        {/* Main sections skeleton */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Urgent — 2/3 width */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <div className="h-3 w-32 bg-slate-200 rounded" />
            </div>
            <div className="divide-y divide-slate-100">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="w-1.5 h-12 bg-slate-200 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-3/4 bg-slate-200 rounded" />
                    <div className="h-3 w-1/2 bg-slate-100 rounded" />
                  </div>
                  <div className="h-5 w-14 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          </div>

          {/* Recent WhatsApp — 1/3 width */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-200">
              <div className="h-3 w-28 bg-slate-300 rounded" />
            </div>
            <div className="divide-y divide-slate-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="px-4 py-3 space-y-2">
                  <div className="flex justify-between">
                    <div className="h-3 w-28 bg-slate-200 rounded" />
                    <div className="h-3 w-10 bg-slate-100 rounded" />
                  </div>
                  <div className="h-3 w-full bg-slate-100 rounded" />
                  <div className="h-3 w-2/3 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Deadlines table skeleton */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <div className="h-3 w-40 bg-slate-200 rounded" />
          </div>
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-6 px-6 py-4">
                <div className="h-3 flex-1 bg-slate-200 rounded" />
                <div className="h-3 w-24 bg-slate-100 rounded" />
                <div className="h-5 w-16 bg-slate-100 rounded" />
                <div className="h-5 w-14 bg-slate-100 rounded" />
                <div className="h-3 w-20 bg-slate-100 rounded ml-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").trim().split(/\s+/)[0];
  const waitingTotal = insights?.replies?.waiting_total || 0;
  const needNow = feed.attention_total || 0;

  const focusAsk = () => {
    const box = document.querySelector('[data-testid="ask-brainydo"]');
    box?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      const input = document.querySelector('[data-testid="ask-brainydo-input"]');
      if (input) input.focus();
      else document.querySelector('[data-testid="ask-brainydo-toggle"]')?.click();
    }, 350);
  };
  const openSearch = () => window.dispatchEvent(new CustomEvent("brainydo:command-palette"));
  const openFocus = () => window.dispatchEvent(new CustomEvent("brainydo:focus-mode"));

  const qa = "h-10 px-3.5 inline-flex items-center gap-2 rounded-xl border text-[13px] font-semibold transition active:scale-95 whitespace-nowrap";

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-[1500px] mx-auto" data-testid="dashboard-page">
      {/* Hero: who needs you + quick actions */}
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 sw-fade-up">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <h1 className="text-[28px] md:text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">
            {greeting}{firstName ? `, ${firstName}` : ""} <span className="inline-block sw-wave origin-[70%_70%]">👋</span>
          </h1>
          <p className="text-[14px] text-slate-500 mt-1 flex flex-wrap gap-x-1.5">
            {needNow || waitingTotal || stats.review_queue ? (
              <>
                {needNow > 0 && <Link to="/app/tasks?bucket=attention" className="font-semibold text-red-600 hover:underline underline-offset-2">{needNow} task{needNow === 1 ? "" : "s"} need{needNow === 1 ? "s" : ""} you now</Link>}
                {needNow > 0 && (waitingTotal > 0 || stats.review_queue > 0) && <span>·</span>}
                {waitingTotal > 0 && <a href="#waiting" className="font-semibold text-amber-700 hover:underline underline-offset-2">{waitingTotal} client{waitingTotal === 1 ? "" : "s"} waiting on a reply</a>}
                {waitingTotal > 0 && stats.review_queue > 0 && <span>·</span>}
                {stats.review_queue > 0 && <Link to="/app/review" className="font-semibold text-violet-700 hover:underline underline-offset-2">{stats.review_queue} to review</Link>}
              </>
            ) : "You're all clear. Nice work — here's how the week is going."}
          </p>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 xl:mx-0 xl:px-0 pb-1 xl:pb-0 [scrollbar-width:none]" data-testid="quick-actions">
          {needNow > 0 && (
            <button onClick={openFocus} className={qa + " border-slate-900 bg-slate-900 text-white hover:bg-slate-800 shadow-sm sw-glow"} data-testid="qa-focus">
              <Target size={15} /> Focus mode <span className="text-[11px] bg-white/20 rounded-full px-1.5 tabular-nums">{needNow}</span>
            </button>
          )}
          <button onClick={() => nav("/app/tasks?create=1")} className={qa + " border-blue-600 bg-blue-600 text-white hover:bg-blue-700"} data-testid="qa-new-task">
            <Plus size={15} /> New task
          </button>
          <Link to="/app/review" className={qa + " border-slate-200 bg-white text-slate-700 hover:border-violet-400 hover:text-violet-700"} data-testid="qa-review">
            <ClipboardCheck size={15} /> Review
            {stats.review_queue > 0 && <span className="text-[11px] font-bold bg-violet-100 text-violet-700 rounded-full px-1.5 tabular-nums">{stats.review_queue}</span>}
          </Link>
          <button onClick={focusAsk} className={qa + " border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"} data-testid="qa-ask">
            <Sparkles size={15} /> Ask
          </button>
          <button onClick={openSearch} className={qa + " border-slate-200 bg-white text-slate-500 hover:border-slate-400"} data-testid="qa-search">
            <Search size={15} /> Search <kbd className="text-[10.5px] font-mono bg-slate-100 border border-slate-200 rounded px-1">⌘K</kbd>
          </button>
        </div>
      </div>

      {/* Auto-mode health */}
      {waHealth && (waHealth.has_auth || waHealth.link_lost_at) && (
        <HealthLine h={waHealth} />
      )}

      {/* Onboarding — shown when WhatsApp not yet connected */}
      {!waConnected && !waHealth?.link_lost_at && (
        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                <span className="material-symbols-outlined text-[13px]">bolt</span>
                Get Started
              </div>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-tight mb-1">
                Connect WhatsApp to activate Brainydo
              </h2>
              <p className="text-[13.5px] text-slate-500 mb-6 max-w-lg">
                Brainydo reads your business WhatsApp messages and automatically creates and prioritises tasks using AI — so nothing falls through the cracks.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                {[
                  { step: "1", icon: "qr_code_scanner", title: "Connect WhatsApp", desc: "Scan a QR code to link your WhatsApp Business number. Takes 30 seconds." },
                  { step: "2", icon: "mark_chat_read", title: "Select Chats", desc: "Choose which chats Brainydo should monitor — groups, clients, or all." },
                  { step: "3", icon: "task_alt", title: "Tasks Created Automatically", desc: "AI reads each message, classifies it, sets priority and deadline, and adds it to your task list." },
                ].map(({ step, icon, title, desc }) => (
                  <div key={step} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{step}</div>
                      <span className="material-symbols-outlined text-blue-600 text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>
                    </div>
                    <div className="text-[13px] font-bold text-slate-900 mb-1">{title}</div>
                    <div className="text-[12px] text-slate-500">{desc}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/app/whatsapp"
                  className="inline-flex items-center gap-2 h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-[14px] transition active:scale-95"
                >
                  <span className="material-symbols-outlined text-[18px]">link</span>
                  Connect WhatsApp Now
                </a>
                <button
                  onClick={seed}
                  disabled={seeding}
                  className="inline-flex items-center gap-2 h-11 px-5 border border-slate-200 hover:border-slate-400 rounded-xl text-[13px] font-medium text-slate-600 transition disabled:opacity-60"
                >
                  <span className="material-symbols-outlined text-[17px]">science</span>
                  {seeding ? "Loading…" : "Try with sample data"}
                </button>
              </div>
            </div>
            <div className="hidden lg:flex flex-col items-center justify-center w-44 shrink-0">
              <div className="w-36 h-36 rounded-2xl bg-blue-600/10 border border-blue-200 flex items-center justify-center">
                <span className="material-symbols-outlined text-blue-600 text-[64px]" style={{ fontVariationSettings: "'FILL' 1" }}>chat</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-3 text-center">Your WhatsApp, intelligently automated</div>
            </div>
          </div>
        </div>
      )}

      {/* Productivity */}
      <ProductivityPanel insights={insights} />

      {/* Open work by category — compact, every chip is a filter */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap [scrollbar-width:none]" data-testid="kpi-grid">
        {STAT_CARDS.map(({ key, label, cardClass, icon, iconColor }, i) => (
          <button key={key}
            onClick={() => nav(key === "review_queue" ? "/app/review" : tabUrlFor(key))}
            className={`${cardClass} shrink-0 h-10 pl-3 pr-3.5 rounded-lg inline-flex items-center gap-2 hover:shadow-sm hover:-translate-y-px transition sw-fade-up`}
            style={{ animationDelay: `${i * 25}ms` }}
            data-testid={`kpi-${key}`}>
            <span className="material-symbols-outlined text-[17px]" style={{ color: iconColor, fontVariationSettings: "'FILL' 1" }}>{icon}</span>
            <span className="text-[12px] font-semibold text-slate-700">{label}</span>
            <span className="text-[14px] font-extrabold text-slate-900 tabular-nums">{stats[key] ?? 0}</span>
          </button>
        ))}
      </div>

      {empty && (
        <div className="border-2 border-dashed border-slate-200 bg-white rounded-xl p-10 text-center sw-fade-up" data-testid="empty-state">
          <span className="material-symbols-outlined text-slate-300 text-5xl" style={{ fontVariationSettings: "'FILL' 1" }}>chat_bubble</span>
          <div className="text-xl font-bold text-slate-800 mt-4">No messages yet</div>
          <p className="text-slate-500 text-[14px] mt-1 max-w-md mx-auto">
            Connect WhatsApp or load sample messages to see Brainydo in action.
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <button onClick={seed} disabled={seeding}
              className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95"
              data-testid="empty-seed-btn"
            >
              {seeding ? "Loading…" : "Load samples"}
            </button>
            <Link to="/app/whatsapp" className="h-10 px-5 inline-flex items-center border border-slate-200 hover:border-blue-600 rounded-xl text-[13px] font-medium transition"
              data-testid="empty-connect-btn">
              Connect WhatsApp
            </Link>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* Needs you now: overdue + due today, act inline */}
          <section className="bg-white rounded-xl border border-slate-200" data-testid="attention-section">
            <div className="px-4 py-3 border-b border-slate-100 bg-red-50/40 rounded-t-xl flex items-center gap-2">
              <span className="relative flex w-2 h-2">
                {needNow > 0 && <span className="absolute inset-0 rounded-full bg-red-400 animate-ping" />}
                <span className={"relative w-2 h-2 rounded-full " + (needNow ? "bg-red-500" : "bg-emerald-500")} />
              </span>
              <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-red-700">Needs you now</h3>
              <span className="text-[12px] text-slate-400">· overdue &amp; due today</span>
              {needNow > 0 && <span className="text-[11px] font-bold text-red-700 bg-red-100 rounded-full px-2 py-0.5 tabular-nums">{needNow}</span>}
              <Link to="/app/tasks?bucket=attention" className="ml-auto text-[12px] text-blue-600 font-semibold hover:text-blue-800 inline-flex items-center gap-1">
                All <ArrowRight size={13} />
              </Link>
            </div>
            <QuickTaskList
              tasks={feed.attention || []}
              onRemoved={dropFrom("attention")}
              onChanged={refresh}
              empty={
                <div className="px-6 py-10 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 grid place-items-center sw-rise-bounce"><PartyPopper size={22} className="text-emerald-600" /></div>
                  <div className="text-[15px] font-bold text-emerald-700 mt-3">All caught up!</div>
                  <div className="text-[12.5px] text-slate-500">Nothing overdue or due today.</div>
                </div>
              }
            />
            {needNow > (feed.attention || []).length && (feed.attention || []).length > 0 && (
              <Link to="/app/tasks?bucket=attention" className="block px-4 py-2.5 text-center text-[12.5px] font-semibold text-blue-600 hover:bg-blue-50/50 border-t border-slate-100 rounded-b-xl">
                + {needNow - feed.attention.length} more — open in Tasks
              </Link>
            )}
          </section>

          {/* What's buzzing in followed groups */}
          <GroupPulse />

          {/* Coming up */}
          <section className="bg-white rounded-xl border border-slate-200" data-testid="upcoming-section">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 rounded-t-xl flex items-center gap-2">
              <CalendarClock size={14} className="text-amber-600" />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700">Coming up</h3>
              <Link to="/app/tasks" className="ml-auto text-[12px] text-blue-600 font-semibold hover:text-blue-800 inline-flex items-center gap-1">
                All tasks <ArrowRight size={13} />
              </Link>
            </div>
            <QuickTaskList
              tasks={feed.upcoming || []}
              onRemoved={dropFrom("upcoming")}
              onChanged={refresh}
              empty={<div className="px-6 py-8 text-center text-[13px] text-slate-500">Nothing scheduled after today.</div>}
            />
          </section>

          {/* Ask Brainydo */}
          <AskBrainydo />
        </div>

        <aside className="space-y-6 min-w-0">
          <div id="waiting" className="scroll-mt-24">
            <WaitingOnYou items={insights?.replies?.waiting || []} total={waitingTotal} />
          </div>
          <DidYouKnow facts={insights?.facts || []} />
          {insights?.tasks?.heatmap && (
            <section className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto">
              <Heatmap days={insights.tasks.heatmap} />
            </section>
          )}

          {/* Recent WhatsApp */}
          <section className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="recent-section">
            <div className="px-4 py-3 bg-[#075E54] text-white flex items-center justify-between">
              <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] flex items-center gap-1.5">
                <MessageCircle size={13} /> Recent WhatsApp
              </h3>
              <Link to="/app/messages" className="text-[11.5px] font-semibold text-white/80 hover:text-white">All chats →</Link>
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              {feed.recent_messages.length === 0 && (
                <div className="px-5 py-8 text-center text-[13px] text-slate-500">No messages yet.</div>
              )}
              {feed.recent_messages.map((m, i) => (
                <Link key={m.id} to={`/app/messages?chat=${encodeURIComponent(m.chat_id || "")}`}
                  className="block px-4 py-2.5 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 sw-fade-up"
                  style={{ animationDelay: `${i * 20}ms` }} data-testid={`recent-msg-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900 truncate">
                      {m.direction === "outgoing" && <span className="text-emerald-600">You → </span>}
                      {m.chat_type === "individual" ? (m.chat_name || m.sender_name) : m.chat_name}
                    </div>
                    <div className="text-[10.5px] text-slate-400 shrink-0">{formatRelative(m.timestamp || m.created_at)}</div>
                  </div>
                  <div className="text-[12px] text-slate-600 line-clamp-1 mt-0.5">
                    {m.chat_type === "group" && m.direction !== "outgoing" && <span className="text-slate-400">{m.sender_name}: </span>}
                    {stripMentionIds(m.text)}
                  </div>
                  {m.classification && m.classification.category && m.classification.category !== "noise" && (
                    <span className="inline-block text-[9px] tracking-[0.16em] uppercase font-bold mt-1 px-1.5 py-px border rounded"
                      style={{ borderColor: categoryColor(m.classification.category), color: categoryColor(m.classification.category) }}>
                      {categoryLabel(m.classification.category)}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <SyncActivityCard counts={counts} phase={phase} onDismiss={dismiss} />
    </div>
  );
}

function tabUrlFor(key) {
  // Each dashboard card lands on the Tasks tab the user actually expects
  switch (key) {
    case "urgent_today":
      return "/app/tasks?tab=active&priority=urgent";
    case "quotations_awaiting":
      return "/app/tasks?tab=active&category=quotation";
    case "pending_followups":
      return "/app/tasks?tab=active&category=follow_up";
    case "new_enquiries":
      return "/app/tasks?tab=active&category=new_enquiry";
    case "client_support":
      return "/app/tasks?tab=active&category=client_support";
    case "opportunities":
      return "/app/tasks?tab=active&category=opportunity";
    case "deadline_commitments":
      return "/app/tasks?tab=active&category=deadline_commitment";
    default:
      return "/app/tasks?tab=active";
  }
}


function relTime(iso) {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function HealthLine({ h }) {
  const modeLabel = h.mode === "always_on" ? "Auto mode · connected 24/7" : h.mode === "schedule" ? "Auto mode · on a schedule" : "Connects while you're here";
  if (h.link_lost_at) {
    return (
      <Link to="/app/whatsapp" className="flex items-center gap-3 p-4 rounded-xl border border-red-200 bg-red-50 hover:border-red-400 transition" data-testid="wa-link-lost">
        <div className="w-9 h-9 rounded-lg bg-white border border-red-200 grid place-items-center shrink-0"><WifiOff size={17} className="text-red-600" /></div>
        <div className="flex-1 min-w-0 text-[13px] text-red-900">
          <span className="font-bold">WhatsApp removed Brainydo as a linked device</span>
          <span className="text-red-700"> — nothing is being read since {relTime(h.link_lost_at)}. Scan the QR code again to resume.</span>
        </div>
        <span className="text-[12.5px] font-semibold text-red-700 shrink-0">Reconnect →</span>
      </Link>
    );
  }
  const down = !h.connected && h.has_auth;
  const scheduledOff = h.mode === "schedule" && h.schedule_open === false;
  return (
    <div className={"flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 rounded-xl border text-[12.5px] " + (h.connected ? "border-emerald-200 bg-emerald-50/60 text-emerald-900" : scheduledOff ? "border-slate-200 bg-slate-50 text-slate-700" : "border-amber-200 bg-amber-50 text-amber-900")} data-testid="wa-health">
      <span className="inline-flex items-center gap-1.5 font-semibold">
        {h.connected ? <Wifi size={14} className="text-emerald-600" /> : scheduledOff ? <Clock size={14} className="text-slate-500" /> : <AlertTriangle size={14} className="text-amber-600" />}
        {h.connected ? modeLabel : scheduledOff ? "Paused by your schedule" : down ? "Reconnecting…" : modeLabel}
      </span>
      {h.connected && h.connected_since && <span className="text-emerald-800/80">Connected since {new Date(h.connected_since).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>}
      {scheduledOff && h.next_change && <span>Back on at {new Date(h.next_change).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}</span>}
      {down && !scheduledOff && h.down_since && <span>Down since {relTime(h.down_since)} — retrying every 2 min</span>}
      <span className="text-slate-500">Last message read {relTime(h.last_message_at) || "—"}</span>
      <span className="text-slate-500">Last notification {relTime(h.last_notification_at) || "—"}</span>
      <Link to="/app/whatsapp" className="ml-auto text-[12px] font-semibold underline-offset-2 hover:underline">Manage</Link>
    </div>
  );
}
